import { ChangeEvent, KeyboardEvent, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { IconPaperclip, IconPlayerStop, IconSend } from '@u21/tabler-icons';
import {
  U21AiChatAttachmentChip,
  U21AttachmentCardStatus,
} from 'app/shared/u21-ui/components/input/chat/U21AiChatAttachmentChip';
import { U21Button } from 'app/shared/u21-ui/components/input/U21Button';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21TextField } from 'app/shared/u21-ui/components/input/text-field/U21TextField';
import { U21AiChatUpload } from 'app/shared/u21-ui/components/input/chat/types';

const getAttachmentCardStatus = (
  upload: U21AiChatUpload,
): U21AttachmentCardStatus => {
  if (upload.status === 'failed') {
    return { message: upload.errorMessage, state: 'failed' };
  }
  if (upload.status === 'done') {
    return { state: 'success' };
  }
  return { state: 'uploading' };
};

interface Props {
  disabled?: boolean;
  isPending: boolean;
  isSendDisabled?: boolean;
  sendDisabledTooltip?: string;
  onRemoveUpload: (localId: string) => void;
  onSend: (message: string, uploadIds: number[]) => Promise<void>;
  onStopClick: () => void;
  onUploadFile: (files: File[]) => void;
  placeholder?: string;
  stopButtonTooltip?: string;
  uploads?: U21AiChatUpload[];
}

export const U21AiChatInput = ({
  disabled = false,
  isPending,
  isSendDisabled = false,
  sendDisabledTooltip,
  onRemoveUpload,
  onSend,
  onStopClick,
  onUploadFile,
  placeholder = 'Type a message...',
  stopButtonTooltip,
  uploads = [],
}: Props) => {
  const [message, setMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isMessageEmpty = useMemo(() => !message.trim(), [message]);
  const canSend = useMemo(
    () => !disabled && !isSendDisabled && !isMessageEmpty,
    [disabled, isSendDisabled, isMessageEmpty],
  );
  const tooltip = useMemo(() => {
    if (isSendDisabled) {
      return sendDisabledTooltip;
    }
    if (isMessageEmpty) {
      return 'You must write a message.';
    }
    return undefined;
  }, [isSendDisabled, isMessageEmpty, sendDisabledTooltip]);

  const handleSend = async () => {
    if (!canSend) {
      return;
    }
    const messageToSend = message.trim();
    const completedUploadIds = uploads
      .filter(
        (u): u is Extract<U21AiChatUpload, { status: 'done' }> =>
          u.status === 'done',
      )
      .map((u) => u.id);
    try {
      await onSend(messageToSend, completedUploadIds);
      setMessage('');
    } catch {
      // Consumer handles error display.
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSend();
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    onUploadFile(selected);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const inputsDisabled = disabled || isPending || isSendDisabled;

  return (
    <U21Spacer spacing={0.5}>
      {uploads.length > 0 && (
        <FileWrapper horizontal spacing={0.5} wrap>
          {uploads.map((upload) => (
            <U21AiChatAttachmentChip
              fileName={upload.name}
              key={upload.localId}
              onRemove={() => onRemoveUpload(upload.localId)}
              status={getAttachmentCardStatus(upload)}
            />
          ))}
        </FileWrapper>
      )}
      <input
        hidden
        multiple
        onChange={handleFileChange}
        ref={fileInputRef}
        type="file"
      />
      <U21Spacer align="end" horizontal>
        <U21Button
          aria-label="Attach files"
          color="inherit"
          disabled={inputsDisabled}
          onClick={() => fileInputRef.current?.click()}
        >
          <IconPaperclip />
        </U21Button>
        <ChatTextField
          clearable={false}
          disabled={inputsDisabled}
          minRows={1}
          onChange={(val) => setMessage(String(val ?? ''))}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          type="textarea"
          value={message}
        />
        {isPending ? (
          <U21Button
            aria-label="Cancel work on current message"
            color="error"
            onClick={onStopClick}
            tooltip={stopButtonTooltip}
            variant="outlined"
          >
            <IconPlayerStop />
          </U21Button>
        ) : (
          <U21Button
            aria-label="Send"
            color="primary"
            disabled={!canSend}
            onClick={handleSend}
            tooltip={tooltip}
            variant="contained"
          >
            <IconSend />
          </U21Button>
        )}
      </U21Spacer>
    </U21Spacer>
  );
};

const FileWrapper = styled(U21Spacer)`
  max-height: 100%;
`;

const ChatTextField = styled(U21TextField)`
  flex: 1;
  min-width: 0;
`;
